import React, { useCallback, useEffect, useRef, useState } from 'react';
import { styled, IconButton, Alert } from '@mui/material';
import { icons } from '../../assets/icons/icons';
import AddPhotoAlternateOutlinedIcon from '@mui/icons-material/AddPhotoAlternateOutlined';
import { colors } from '../../constants/colors';
import { useDropzone } from 'react-dropzone';
import { alertError } from '../alert/swal';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import PreviewImage from '../preview-image/PreviewImage';

const UploadContainer = styled('div')({
    width: 'auto',
});

const LayoutImage = styled('span')<{ isDragActive: boolean }>(({ isDragActive }) => ({
    width: '164px',
    height: '164px',
    borderRadius: '50%',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: isDragActive ? '#e0e0e0' : '#F2F4F7',
    border: '10px solid #fff',
    outline: '2px solid #F2F4F7',
    position: 'relative',
    transition: 'all .5s ease',
}));

type Props = {
    file: any;
    onDrop: (file: any) => void;
    onDelete?: () => void;
    noClick?: boolean;
    helperText?: string;
    filePath?: string;
    fileName?: string;
    fileId?: string;
    hiddenDeleteButton?: boolean;
};

const UploadAvatar: React.FC<Props> = (props: Props) => {
    const { file, onDelete, helperText } = props;
    const helperTextRef = useRef<HTMLDivElement>(null);
    const [loaded, setLoaded] = useState<boolean>(false);

    const pathUrl = process.env.REACT_APP_FILE_BASE_API;
    const allowedTypes: string[] = ['image/jpeg', 'image/jpg', 'image/png'];

    const onDrop = useCallback((acceptedFiles: any) => {
        const file = acceptedFiles[0];
        if (file) {
            if (!allowedTypes.includes(file.type)) {
                alertError('รูปแบบไฟล์ไม่ถูกต้องกรุณาตรวจสอบ');
                return;
            }
            const maxSize = 10 * 1024 * 1024; // 10 MB
            if (file.size > maxSize) {
                alertError('ขนาดไฟล์ใหญ่เกินไป');
                return;
            }

            props.onDrop(acceptedFiles);
        }
    }, [props]);

    const { getRootProps, getInputProps, isDragActive, isDragReject } = useDropzone({
        onDrop,
        maxFiles: 1,
        accept: {
            'image/png': ['.png', '.jpg', '.jpeg'],
        },
        ...(props.noClick ? { noClick: true } : {}),
    });

    useEffect(() => {
        if (helperText && helperTextRef.current) {
            helperTextRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
    }, [helperText]);

    return (
        <UploadContainer>
            {props.filePath && (
                <LayoutImage
                    className={`${props.hiddenDeleteButton && 'hover:opacity-70 cursor-pointer'}`}
                    isDragActive={isDragActive} sx={{ overflow: 'hidden' }}>
                    {!props.hiddenDeleteButton && (
                        <IconButton
                            onClick={onDelete}
                            sx={{
                                width: 30,
                                height: 30,
                                zIndex:999,
                                position: 'absolute',
                                top: '50%',
                                left: '50%',
                                transform: 'translate(-50%,-50%)',
                                backgroundColor: '#82828287',
                            }}
                        >
                            <HighlightOffIcon sx={{ width: 18, height: 18, color: colors.white }} />
                        </IconButton>
                    )}
                    <PreviewImage
                        disable_show={!props.hiddenDeleteButton}
                        render_image={
                            <img
                                style={{
                                    transition: 'filter 0.6s ease-out',
                                    filter: loaded ? 'none' : 'blur(10px)',
                                    width: "100%",
                                    height: "100%",
                                    objectFit: 'cover',
                                    objectPosition: 'center',
                                }}
                                onLoad={() => setLoaded(true)}
                                src={`${pathUrl}/${props.filePath}`}
                                alt="profile"
                            />}
                        image_url={`${pathUrl}/${props.filePath}`} />
                    {/* <img
                        style={{
                            transition: 'filter 0.6s ease-out',
                            filter: loaded ? 'none' : 'blur(10px)',
                            width: "100%",
                            height: "100%",
                            objectFit: 'cover',
                            objectPosition: 'center',
                        }}
                        // width={250}
                        // height={250}
                        onLoad={() => setLoaded(true)}
                        src={`${pathUrl}/${props.filePath}`}
                        alt="profile"
                    /> */}
                </LayoutImage>
            )}
            {file && !props.filePath ? (
                <LayoutImage isDragActive={isDragActive} sx={{
                    overflow: 'hidden',
                    backgroundImage: `url(${URL.createObjectURL(props.file)})`,
                    backgroundSize: 'cover',
                    backgroundRepeat: "no-repeat",
                }}>
                    {!props.hiddenDeleteButton && (
                        <IconButton
                            onClick={onDelete}
                            sx={{
                                width: 30,
                                height: 30,
                                position: 'absolute',
                                top: '50%',
                                left: '50%',
                                transform: 'translate(-50%,-50%)',
                                backgroundColor: '#82828287',
                            }}
                        >
                            <HighlightOffIcon sx={{ width: 18, height: 18, color: colors.white }} />
                        </IconButton>
                    )}
                    {/* <img
                        className='border-2 border-red-600'
                        width={250}
                        height={250}
                        style={{ objectFit: 'cover' }}
                        src={URL.createObjectURL(props.file)}
                        alt="profile"
                    /> */}
                </LayoutImage>
            ) : (
                <>
                    {isDragActive && !isDragReject ? (
                        <LayoutImage isDragActive={isDragActive}>วางไฟล์ที่นี่</LayoutImage>
                    ) : (
                        <>
                            {!props.filePath && (
                                <LayoutImage {...getRootProps()} isDragActive={isDragActive} sx={{ cursor: 'pointer' }}>
                                    <input {...getInputProps()} />
                                    <IconButton
                                        sx={{
                                            width: 30,
                                            height: 30,
                                            position: 'absolute',
                                            bottom: 10,
                                            right: -7,
                                            backgroundColor: '#82828287',
                                        }}
                                    >
                                        <AddPhotoAlternateOutlinedIcon sx={{ width: 18, height: 18, color: colors.white }} />
                                    </IconButton>
                                    <img width={100} height={100} src={icons.profile} alt="profile" />
                                </LayoutImage>
                            )}
                        </>
                    )}
                </>
            )}
            {helperText && (
                <Alert className="my-4" ref={helperTextRef} tabIndex={-1} severity="error">
                    {helperText}
                </Alert>
            )}
        </UploadContainer>
    );
};

export default UploadAvatar;
